import { useRef } from "react";
import styles from "./EventCodeInput.module.css";

type EventCodeInputProps = {
  value: string;
  onChange: (next: string) => void;
  length?: number;
  labelledBy?: string;
};

const EventCodeInput: React.FC<EventCodeInputProps> = ({
  value,
  onChange,
  length = 6,
  labelledBy,
}) => {
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const chars = Array.from({ length }, (_, i) => value[i] ?? "");

  const setAt = (index: number, char: string): void => {
    const next = chars.map((c, i) => (i === index ? char : c));
    onChange(next.join("").replace(/\s/g, "").toUpperCase());
  };

  return (
    <div className={styles.code} role="group" aria-labelledby={labelledBy}>
      {chars.map((char, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          maxLength={1}
          aria-label={`${i + 1}`}
          autoComplete="off"
          inputMode="text"
          value={char}
          className={char ? styles.filled : undefined}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^a-z0-9]/gi, "").toUpperCase();
            const last = raw.slice(-1);
            setAt(i, last);
            if (last && refs.current[i + 1]) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === "Backspace" && !char && refs.current[i - 1]) {
              refs.current[i - 1]?.focus();
            }
          }}
          onPaste={(e) => {
            e.preventDefault();
            const pasted = (e.clipboardData.getData("text") || "")
              .replace(/[^a-z0-9]/gi, "")
              .toUpperCase()
              .slice(0, length);
            onChange(pasted);
            refs.current[Math.min(pasted.length, length - 1)]?.focus();
          }}
        />
      ))}
    </div>
  );
};

export default EventCodeInput;
